import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import PasswordInput from "@/components/ui/PasswordInput";
import { showApiError, showSuccess } from "@/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const generateSecurePassword = (length = 14) => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%^&*";
  if (typeof window !== "undefined" && window.crypto && window.crypto.getRandomValues) {
    const randomValues = new Uint32Array(length);
    window.crypto.getRandomValues(randomValues);
    let result = "";
    for (let i = 0; i < length; i++) {
      result += chars[randomValues[i] % chars.length];
    }
    return result;
  }
  let result = "";
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

const InviteMemberModal = ({
  show,
  onClose,
  clientId,
  adminEmail = "",
  isSuperAdmin = false,
  onUserCreated,
}) => {
  const { t } = useTranslation();

  const adminDomain = adminEmail.includes("@")
    ? adminEmail.split("@")[1].toLowerCase()
    : "";

  const [formData, setFormData] = useState({
    firstname: "",
    lastname: "",
    email: "",
    password: generateSecurePassword(),
    profile_picture: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createdUserData, setCreatedUserData] = useState(null);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const resetForm = () => {
    setFormData({
      firstname: "",
      lastname: "",
      email: "",
      password: generateSecurePassword(),
      profile_picture: "",
    });
    setCreatedUserData(null);
    setEmailSent(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleGenerateNewPassword = () => {
    setFormData((prev) => ({
      ...prev,
      password: generateSecurePassword(),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const emailTrimmed = formData.email.trim().toLowerCase();
    const userDomain = emailTrimmed.includes("@")
      ? emailTrimmed.split("@")[1].toLowerCase()
      : "";

    if (!isSuperAdmin && adminDomain && userDomain !== adminDomain) {
      showApiError(
        t("members.emailDomainError", "Email must have domain @{{domain}}", {
          domain: adminDomain,
        }),
      );
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        email: emailTrimmed,
        firstname: formData.firstname.trim(),
        lastname: formData.lastname.trim(),
        password: formData.password,
        client_id: clientId,
        profile_picture: formData.profile_picture.trim() || null,
      };

      const response = await fetch(`${BACKEND_URL}/api/v1/user`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        showSuccess(result.message || t("members.inviteSuccess", "User invited successfully!"));
        setCreatedUserData({
          email: emailTrimmed,
          username: result.username,
          emailBody: result.email_body,
        });

        if (typeof onUserCreated === "function") {
          onUserCreated();
        }
      } else {
        showApiError(
          result.message || result.detail || "Failed to create user",
        );
      }
    } catch (err) {
      console.error("Error creating user:", err);
      showApiError("An unexpected error occurred while creating the user.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendWelcomeEmail = async () => {
    if (!createdUserData) return;

    setIsSendingEmail(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/user/send-email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          to_email: createdUserData.email,
          subject: "Welcome to elelem",
          body: createdUserData.emailBody,
        }),
      });

      const result = await response.json();
      if (response.ok && result.success) {
        showSuccess(result.message || "Welcome email sent successfully!");
        setEmailSent(true);
      } else {
        showApiError(result.message || result.detail || "Failed to send welcome email.");
      }
    } catch (err) {
      console.error("Error sending welcome email:", err);
      showApiError("Failed to dispatch welcome email.");
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleCopyCredentials = async () => {
    if (!createdUserData) return;
    const creds = `Email: ${createdUserData.email}\nTemporary Password: ${formData.password}\nLogin: https://geo.elelem.ai/`;
    try {
      await navigator.clipboard.writeText(creds);
      showSuccess("Credentials copied to clipboard!");
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  if (!show) return null;

  return (
    <div
      className="modal fade show"
      style={{ display: "block", backgroundColor: "rgba(8, 38, 49, 0.45)", backdropFilter: "blur(4px)" }}
      tabIndex="-1"
      role="dialog"
      aria-modal="true"
    >
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content card-radius border-0 shadow-lg">
          <div className="modal-header border-bottom px-4 pt-4 pb-3">
            <h5 className="modal-title fw-bold" style={{ color: "var(--brand-navy)" }}>
              {createdUserData
                ? t("members.inviteConfirmation", "Member Provisioned")
                : t("members.inviteMember", "Invite New Member")}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={handleClose}
              disabled={isSubmitting || isSendingEmail}
              aria-label="Close"
            />
          </div>

          {!createdUserData ? (
            <form onSubmit={handleSubmit}>
              <div className="modal-body px-4 py-3">
                {/* Organization info lock badge */}
                <div className="mb-3 p-2 px-3 rounded bg-light border d-flex justify-content-between align-items-center">
                  <div className="small text-secondary">
                    <i className="bi bi-building me-1 text-info" />
                    <strong>{t("members.client", "Organization")}:</strong> {clientId}
                  </div>
                  {!isSuperAdmin && adminDomain ? (
                    <span className="badge bg-info-subtle text-info-emphasis border border-info-subtle">
                      @{adminDomain}
                    </span>
                  ) : isSuperAdmin ? (
                    <span className="badge bg-secondary-subtle text-secondary border border-secondary-subtle">
                      {t("members.unrestricted", "Super Admin (Unrestricted)")}
                    </span>
                  ) : null}
                </div>

                <div className="row g-3">
                  <div className="col-md-6">
                    <label htmlFor="invite-firstname" className="form-label small fw-semibold text-secondary">
                      {t("members.firstName", "First Name")} <span className="text-danger">*</span>
                    </label>
                    <input
                      id="invite-firstname"
                      type="text"
                      className="form-control"
                      name="firstname"
                      value={formData.firstname}
                      onChange={handleChange}
                      placeholder="e.g. Alex"
                      required
                      disabled={isSubmitting}
                      data-testid="input-firstname"
                    />
                  </div>

                  <div className="col-md-6">
                    <label htmlFor="invite-lastname" className="form-label small fw-semibold text-secondary">
                      {t("members.lastName", "Last Name")} <span className="text-danger">*</span>
                    </label>
                    <input
                      id="invite-lastname"
                      type="text"
                      className="form-control"
                      name="lastname"
                      value={formData.lastname}
                      onChange={handleChange}
                      placeholder="e.g. Turner"
                      required
                      disabled={isSubmitting}
                      data-testid="input-lastname"
                    />
                  </div>

                  <div className="col-md-12">
                    <label htmlFor="invite-email" className="form-label small fw-semibold text-secondary">
                      {t("members.workEmail", "Work Email")} <span className="text-danger">*</span>
                    </label>
                    <input
                      id="invite-email"
                      type="email"
                      className="form-control"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder={!isSuperAdmin && adminDomain ? `member@${adminDomain}` : "user@company.com"}
                      required
                      disabled={isSubmitting}
                      data-testid="input-email"
                    />
                    {!isSuperAdmin && adminDomain && (
                      <div className="form-text small text-muted">
                        {t("members.allowedDomainHelper", "Users must have a corporate @{{domain}} address", {
                          domain: adminDomain,
                        })}
                      </div>
                    )}
                  </div>

                  <div className="col-md-12">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <label htmlFor="invite-member-password" className="form-label small fw-semibold text-secondary mb-0">
                        {t("members.tempPassword", "Temporary Password")} <span className="text-danger">*</span>
                      </label>
                      <button
                        type="button"
                        className="btn btn-link p-0 small text-decoration-none"
                        style={{ fontSize: "0.8rem", color: "var(--brand-color-dark)" }}
                        onClick={handleGenerateNewPassword}
                      >
                        <i className="bi bi-arrow-clockwise me-1" />
                        {t("members.generateNew", "Generate Random")}
                      </button>
                    </div>
                    <PasswordInput
                      id="invite-member-password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      disabled={isSubmitting}
                      data-testid="input-password"
                    />
                  </div>
                </div>
              </div>

              <div className="modal-footer border-top px-4 py-3 bg-light">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm px-3 rounded-pill"
                  onClick={handleClose}
                  disabled={isSubmitting}
                >
                  {t("common.cancel", "Cancel")}
                </button>
                <button
                  type="submit"
                  className="btn-invite-pill"
                  disabled={
                    isSubmitting ||
                    !formData.firstname.trim() ||
                    !formData.lastname.trim() ||
                    !formData.email.trim() ||
                    !formData.password.trim()
                  }
                  data-testid="btn-submit-invite"
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                      {t("common.submitting", "Inviting...")}
                    </>
                  ) : (
                    <>
                      <i className="bi bi-person-plus me-1" />
                      {t("members.sendInvite", "Invite Member")}
                    </>
                  )}
                </button>
              </div>
            </form>
          ) : (
            <div className="modal-body p-4">
              <div className="alert alert-success d-flex align-items-center mb-3">
                <i className="bi bi-check-circle-fill fs-4 me-3 text-success" />
                <div>
                  <h6 className="mb-0 fw-bold">{t("members.userCreatedTitle", "Account Ready!")}</h6>
                  <small className="text-secondary">
                    {t("members.userCreatedSubtitle", "The user account for {{email}} has been successfully created.", {
                      email: createdUserData.email,
                    })}
                  </small>
                </div>
              </div>

              <div className="card bg-light border mb-3">
                <div className="card-body p-3">
                  <h6 className="small fw-bold text-secondary mb-2">
                    {t("members.credentialsTitle", "Login Credentials:")}
                  </h6>
                  <div className="small font-monospace mb-1">
                    <strong>Email:</strong> {createdUserData.email}
                  </div>
                  <div className="small font-monospace">
                    <strong>Password:</strong> {formData.password}
                  </div>
                </div>
              </div>

              <div className="d-flex gap-2 justify-content-end">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm rounded-pill"
                  onClick={handleCopyCredentials}
                >
                  <i className="bi bi-clipboard me-1" />
                  {t("members.copyCredentials", "Copy Credentials")}
                </button>

                {!emailSent ? (
                  <button
                    type="button"
                    className="btn-invite-pill"
                    onClick={handleSendWelcomeEmail}
                    disabled={isSendingEmail}
                  >
                    {isSendingEmail ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                        {t("members.sendingEmail", "Sending...")}
                      </>
                    ) : (
                      <>
                        <i className="bi bi-send me-1" />
                        {t("members.sendWelcomeEmail", "Send Welcome Email")}
                      </>
                    )}
                  </button>
                ) : (
                  <span className="badge bg-success-subtle text-success-emphasis border border-success-subtle p-2">
                    <i className="bi bi-check2 me-1" />
                    {t("members.emailSentBadge", "Welcome email sent!")}
                  </span>
                )}

                <button
                  type="button"
                  className="btn btn-secondary btn-sm rounded-pill px-3 ms-2"
                  onClick={handleClose}
                >
                  {t("common.done", "Done")}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InviteMemberModal;
